import React, { useCallback, useMemo, useContext, useRef, useState, useEffect, Fragment } from 'react';
import { CurrentPageContext, HistoryStackContext } from 'app/home/general-components/HistoryStackContext';
import { DeviceList } from 'app/home/main-pages/devices/Devices'
import { BindList } from 'app/home/main-pages/binds/Binds';
import { scenarioIcons } from 'app/model/Scenario';
import Popup, { usePopup } from 'app/home/general-components/Popup/Popup';
import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';
import { DeviceType, WCScenario } from 'app/model/Device';
import { ChooseNameAndIconInner } from './ChooseNameAndIcon';
import { isiOSOrMac } from 'app/home/general-components/BrowserUtils';
import CustomIconBack from 'app/home/home-components/CustomIconBack';

import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconBackBlack } from 'images/general/back-black.svg';
import { ReactComponent as IconPlay } from 'images/scenario/icon-play.svg';
import { ReactComponent as IconDeleteRed } from 'images/general/delete-red.svg';
import { ReactComponent as IconEditDevice } from 'images/setting/settings-gears.svg';
import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconSettingsGear } from 'images/setting/settings-gear.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';

import IconSiriEn1 from 'images/siri/integration-button/siri-en.png';
import IconSiriEn2 from 'images/siri/integration-button/siri-en@2x.png';
import IconSiriEn3 from 'images/siri/integration-button/siri-en@3x.png';
import IconSiriFa1 from 'images/siri/integration-button/siri-fa.png';
import IconSiriFa2 from 'images/siri/integration-button/siri-fa@2x.png';
import IconSiriFa3 from 'images/siri/integration-button/siri-fa@3x.png';


function generateListOfDevicesForScenarioOverview(deviceActions, devices) {
    let dd = [];
    for (let i = 0; i < deviceActions.length; i++) {
        const deviceState = deviceActions[i];
        for (let j = 0; j < devices.length; j++) {
            const device = devices[j];
            if (device.mac === deviceState.mac) {
                if (device.type === DeviceType.evaporativeCooler) { // cooler
                    // console.log(deviceState);
                    let deviceStateToShow = { ...deviceState };
                    // if (deviceState.hasOwnProperty("power")) {
                    if (Object.prototype.hasOwnProperty.call(deviceState, "power")) {
                        deviceStateToShow["power"] = (deviceState["power"] === true) ? 2 : 0;
                        deviceStateToShow["pump"] = (deviceState["power"] === true);
                        deviceStateToShow["slow_fan"] = false;
                        deviceStateToShow["fast_fan"] = false;
                    } else {
                        const on = deviceStateToShow["pump"] || deviceStateToShow["slow_fan"] || deviceStateToShow["fast_fan"]
                        deviceStateToShow["power"] = on ? 1 : 0;
                    }
                    dd.push({
                        ...deviceStateToShow,
                        waitingForChangeState: {},
                        name: device.name,
                        active: true,
                    })
                } else if (device.type === DeviceType.wc) { // wc
                    let deviceStateToShow = { ...deviceState };
                    if (Object.prototype.hasOwnProperty.call(deviceState, "scenario")) {
                        if (deviceState["scenario"] === WCScenario.in_use) {
                            deviceStateToShow["lamp"] = true;
                            deviceStateToShow["fan"] = true;
                            deviceStateToShow["spray"] = false;
                            deviceStateToShow["music"] = (device["music_in_scenario"] ? true : device["music"]);
                        } else if (deviceState["scenario"] === WCScenario.refresh) { 
                            deviceStateToShow["lamp"] = false;
                            deviceStateToShow["fan"] = true;
                            deviceStateToShow["spray"] = true;
                            deviceStateToShow["music"] = (device["music_in_scenario"] ? false : device["music"]);
                        }
                    }
                    dd.push({
                        ...deviceStateToShow,
                        waitingForChangeState: {},
                        name: device.name,
                        active: true,
                    })
                } else if (device.use_custom_switch_names !== undefined) { // lamp/dpst/lampX3/lampX4/relay4: has custom name
                    dd.push({
                        use_custom_switch_names: device.use_custom_switch_names,
                        switch1_name: device.switch1_name,
                        switch2_name: device.switch2_name,
                        switch3_name: device.switch3_name,
                        switch4_name: device.switch4_name,

                        ...deviceState,
                        waitingForChangeState: {},
                        name: device.name,
                        active: true,
                    })
                } else { // else: add to list without modification
                    dd.push({
                        ...deviceState,
                        waitingForChangeState: {},
                        name: device.name,
                        active: true,
                    })
                }
            }
        }
    }
    for (let i = 0; i < devices.length; i++) { // remote
        const remoteDevice = devices[i];
        if ((remoteDevice.type === DeviceType.tuyaRemote) || (remoteDevice.type === DeviceType.remote)) {
            let chosenSubDevices = [];
            for (let j = 0; j < remoteDevice.sub_devices.length; j++) { // subDevice in remote
                const subDevice = remoteDevice.sub_devices[j];
                for (let k = 0; k < deviceActions.length; k++) { // scenario devices
                    const deviceState = deviceActions[k];
                    if (subDevice.mac === deviceState.mac) {
                        chosenSubDevices.push({ // add to selected subdevices
                            ...deviceState,
                            waitingForChangeState: {},
                            name: subDevice.name,
                        })
                    }
                }
            }
            if (chosenSubDevices.length > 0) {
                dd.push({ // add to selected devices
                    sub_devices: chosenSubDevices,
                    waitingForChangeState: {},
                    name: remoteDevice.name,
                    type: remoteDevice.type,
                    active: true,
                })
            }
        }
    }
    // console.log(dd);
    return dd;
}

export default function Overview(
    { pageData, setPageData, back, dismissHandler,
        deviceActions, devices, scenarios, bindStates, binds,
        editMode, editCallback,
        setName, name, setIconId, iconId }) {

    const s = useLocalizedStrings();
    const sc = s.createScenarioProcess;

    const devicesForList = useMemo(() => {
        return generateListOfDevicesForScenarioOverview(deviceActions, devices);
    }, [deviceActions, devices]);

    const bindsForList = useMemo(() => {
        let bb = [];
        for (let i = 0; i < bindStates.length; i++) {
            const bindState = bindStates[i];
            for (let j = 0; j < binds.length; j++) {
                const bind = binds[j];
                if (bind.id === bindState.id) {
                    let b = { ...bind };
                    b.enable = bindState.enable;
                    bb.push(b);
                }
            }
        }
        return bb;
    }, [bindStates, binds]);

    const nextClick = useCallback(() => {
        if (devicesForList.length + bindsForList.length > 0)
            setPageData({ name: 'choose-name-and-icon', from: pageData.name });
    }, [pageData, setPageData, bindsForList.length, devicesForList.length]);

    const doneClick = useCallback(() => {
        if ((devicesForList.length + bindsForList.length > 0) && (name.length > 1)) {
            editCallback();
            dismissHandler();
        }
    }, [editCallback, dismissHandler, devicesForList.length, bindsForList.length, name.length]);

    return (
        <div className="create-scenario-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />

            <div className="header">{editMode ? sc.editScenario : sc.overview}</div>

            <div className="overview-lists-container with-full-button-margin">
                {editMode ? <ChooseNameAndIconInner
                    setName={setName} name={name}
                    setIconId={setIconId} iconId={iconId} /> : null}

                <div className="section">
                    <div className="header">
                        <div className="title bold-header">{sc.devices}</div>
                        <div />
                        <div className="button"
                            onClick={() => {
                                setPageData({ name: 'select-devices', from: pageData.name, forEdit: true });
                            }}>
                            <div className="label">{sc.edit}</div>
                            <IconEditDevice className="icon" />
                        </div>
                    </div>
                    <div className="content">
                        <DeviceList
                            noDeviceText={sc.noDevice}
                            style={{ overflow: "hidden" }}
                            devices={devicesForList} canStateBeNoChange={true}
                            bottomPaddingSize={0}
                            showStates={true}
                            dontOpenSettings={true}
                            showSubDevices={true} />
                    </div>
                </div>

                <div className="section">
                    <div className="header">
                        <div className="title bold-header">{sc.binds}</div>
                        <div />
                        <div className="button"
                            onClick={() => {
                                setPageData({ name: 'select-binds', from: pageData.name, forEdit: true });
                            }}>
                            <div className="label">{sc.edit}</div>
                            <IconEditDevice className="icon" />
                        </div>
                    </div>
                    <div className="content">
                        <BindList
                            devices={devices}
                            scenarios={scenarios}
                            noBindText={sc.noBind}
                            style={{ overflow: "hidden" }}
                            binds={bindsForList}
                            bottomPaddingSize={0}
                            selectionCallback={() => { }} />
                    </div>
                </div>

                {/* <div className="safe-area-padding" /> */}
            </div >

            {!(devicesForList.length + bindsForList.length > 0) ? // if device & bind are empty
                <div className="error-label-for-full-button">{sc.youMustSelectADOB}</div> : (
                    !(name.length > 0) ? // if name is empty
                        <div className="error-label-for-full-button">{sc.youMustEnterAName}</div> : null
                )}

            {
                editMode ?
                    <div className="blue-button fix-on-bottom" onClick={doneClick}
                        style={{ opacity: ((devicesForList.length + bindsForList.length > 0) && (name.length > 1)) ? 1 : 0.3 }}>
                        <div>{s.save}</div>
                        <IconTick className="icon-not-rotating" />
                    </div> :
                    <div className="blue-button next-button" onClick={nextClick}
                        style={{ opacity: (devicesForList.length + bindsForList.length > 0) ? 1 : 0.3 }}>
                        <div>{sc.next}</div>
                        <IconBackBlack className="icon-180" />
                    </div>
            }
        </div >
    );
}


export function ScenarioDetail({ gateway }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const sc = s.createScenarioProcess;

    const hs = useContext(HistoryStackContext);
    const p = useContext(CurrentPageContext);

    const dismissHandler = p.dismiss;
    const scenario = useMemo(() => {
        return gateway.scenarios.find(({ id }) => id === p.state);
    }, [p, gateway.scenarios]);

    const [scenarioCache, setScenarioCache] = useState(scenario);
    useEffect(() => {
        if (scenario === undefined) {
            dismissHandler();
        } else {
            setScenarioCache(scenario);
        }
    }, [dismissHandler, scenario]);

    const [popupId, showPopup, dismissPopup] = usePopup();

    const removeClick = useCallback(() => {
        dismissPopup();
        setTimeout(() => {
            dismissHandler();
            scenarioCache.remove();
        });
    }, [dismissHandler, scenarioCache, dismissPopup])

    const deviceActions = scenarioCache.device_actions;
    const bindStates = scenarioCache.bind_states;

    const devicesForList = useMemo(() => {
        return generateListOfDevicesForScenarioOverview(deviceActions, gateway.devices);
    }, [deviceActions, gateway.devices]);

    const bindsForList = useMemo(() => {
        let bb = [];
        for (let i = 0; i < bindStates.length; i++) {
            const bindState = bindStates[i];
            for (let j = 0; j < gateway.binds.length; j++) {
                const bind = gateway.binds[j];
                if (bind.id === bindState.id) {
                    let b = { ...bind };
                    b.enable = bindState.enable;
                    bb.push(b);
                }
            }
        }
        return bb;
    }, [bindStates, gateway.binds]);

    let scenarioIcon = scenarioIcons[0][0];
    if (scenarioCache.pic_id <= 9) {
        scenarioIcon = scenarioIcons[scenarioCache.pic_id - 1][0]
    }

    const buttonRef = useRef(null);
    const [runClickActive, setRunClickActive] = useState(true);
    const registerScenarioReport = gateway.scenarioReports.register;
    const runClickH = useCallback(() => {
        if (runClickActive) {
            registerScenarioReport(scenarioCache);
            scenarioCache.run();
            buttonRef.current.className = "scenario-cell-play-button flashing";
            setTimeout(() => {
                try {
                    setRunClickActive(true);
                    buttonRef.current.className = "scenario-cell-play-button";
                }
                catch (err) {
                    console.log(err);
                }
            }, 1500 * 3 + 100);
            setRunClickActive(false);
        }
    }, [scenarioCache, runClickActive, registerScenarioReport])


    const onEditClick = useCallback(() => {
        hs.push("/scenarios/create_or_edit", { editMode: true, editScenarioBase: scenarioCache });
    }, [hs, scenarioCache]);

    const onSiriClick = useCallback(() => {
        hs.push("/scenarios/siri_run_scenario", { scenarioId: scenarioCache.id });
    }, [hs, scenarioCache]);

    const showSiriIntegrationButton =
        isiOSOrMac() &&
        gateway.negotiatedProtocol.supportSiriToRunScenario &&
        gateway.currentUser.permissions.scenario.run &&
        (gateway.soloScenarioAuthToken !== undefined) &&
        gateway.isSolo &&
        ((l.language === 'en') || (l.language === 'fa'))

    const headerGroup = useMemo(() => {
        return (
            <div className="header-group">
                <CustomIconBack className="back-icon" />
                <div />
                {
                    gateway.negotiatedProtocol.canEditScenario &&
                        gateway.currentUser.permissions.scenario.setting ? (
                        <Fragment>
                            {
                                showSiriIntegrationButton ? (
                                    l.language === 'en' ?
                                        <img onClick={onSiriClick} src={IconSiriEn1}
                                            srcSet={`${IconSiriEn2} 2x, ${IconSiriEn3} 3x`}
                                            className="siri-icon" alt="" /> :
                                        <img onClick={onSiriClick} src={IconSiriFa1}
                                            srcSet={`${IconSiriFa2} 2x, ${IconSiriFa3} 3x`}
                                            className="siri-icon" alt="" />
                                ) : null
                            }
                            <div className="button-border" onClick={onEditClick}>
                                <div className="label">{s.edit}</div>
                                <IconSettingsGear className="icon" />
                            </div>
                            <IconDeleteWhite className="remove-icon" onClick={showPopup} />
                        </Fragment>
                    ) : null
                }
            </div>
        );
    }, [showPopup, onEditClick, onSiriClick, s.edit, l.language,
        showSiriIntegrationButton,
        gateway.negotiatedProtocol, gateway.currentUser]);

    return (
        <div className="create-scenario-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="create-scenario-page-content">
                {headerGroup}
                <div className="nav-button" onClick={dismissHandler} />

                <div className="header-box">
                    <div className="header-box-2">
                        {React.createElement(scenarioIcon, { className: "icon" })}
                        <div className="header">{scenarioCache.name}</div>
                    </div>
                    <div />
                    {
                        (gateway.currentUser.permissions.scenario.run) ? (
                            <div className="scenario-cell-play-button"
                                ref={buttonRef} onClick={runClickH}>
                                <IconPlay />
                            </div>
                        ) : null
                    }

                </div>

                <div className="overview-lists-container">
                    <div className="header-2" style={{ marginBottom: "8px" }}>{sc.devices}</div>
                    <DeviceList
                        style={{ overflow: "hidden" }}
                        devices={devicesForList} canStateBeNoChange={true}
                        bottomPaddingSize={0}
                        showStates={true}
                        dontOpenSettings={true}
                        showSubDevices={true} />

                    <div className="header-2">{sc.binds}</div>
                    <BindList
                        devices={gateway.devices}
                        scenarios={gateway.scenarios}
                        style={{ overflow: "hidden" }}
                        binds={bindsForList}
                        bottomPaddingSize={0}
                        selectionCallback={() => { }} />
                    {/* <div className="safe-area-padding" /> */}
                </div>

                {/* {
                    (gateway.currentUser.permissions.scenario.setting) ? (
                        <div className="detele-button" onClick={openPopup}>
                            <div className="label">{sc.delete}</div> <IconDeleteRed className="icon" />
                        </div>
                    ) : null
                } */}

                <Popup popupId={popupId} name="remove_scenario_popup" baseZIndex={12}>
                    <div className="scenario-remove-popup">
                        <div className="header">{sc.areYouSure}</div>
                        <div className="buttons-group">
                            <div className="delete-button" onClick={removeClick}>
                                <div className="label">{sc.deleteIt}</div>
                                <IconDeleteRed className="icon" />
                            </div>
                            <div className="dismiss-button" onClick={dismissPopup}>{sc.keepIt}</div>
                        </div>
                    </div>
                </Popup>
            </div>
        </div>
    );
}